import {
    BinocularsIcon,
    BoatIcon,
    CaretDownIcon,
    CaretUpIcon,
    ImageBroken,
    ImageIcon,
    LifebuoyIcon,
} from '@phosphor-icons/react';
import { Button, Copy, VText } from '@vake/ui-kit';
import React, { FC, ReactNode, useState } from 'react';
import { Popup } from 'react-map-gl';
import styled from 'styled-components';
import { SendToMattermost } from '../../pages/Report/components/send-to-mattermost';
import { useDetectionImage, useEarthEngineImage, usePlanetThumbnail } from '../../requests/image';
import { useTimeAtPointInAcqusition } from '../../requests/reports';
import { latLonTextFormat } from '../../utils/date';
import { ReportType } from '../../utils/enums';
import { AcquisitionPopUpInfo } from '../../utils/interfaces';
import { convertDecimalToDM } from '../../utils/location';
import { ImageSource, ImageSourceValues } from '../api-enums';
import { ButtonLink } from '../styles/Buttons';
import { MiniSpinner, Spinner } from '../styles/Spinner';

const PopupDiv = styled.div`
    display: flex;
    flex-direction: column;
    font-family: Arial, Helvetica, sans-serif;
    min-width: 220px;
`;

const SpinnerWrapper = styled.div`
    width: 100%;
    height: 120px;
    align-self: center;
`;

const StyledImage = styled.img`
    align-self: center;
    width: 100%;
`;

const LinkInPopup = styled(ButtonLink)`
    color: white;
    padding: 2%;
    align-self: center;
`;

export const DetectionImage: React.FC<{ image_path: string; alt?: string | undefined }> = ({ image_path, alt }) => {
    const { data, isLoading } = useDetectionImage(image_path);
    if (isLoading) {
        return (
            <SpinnerWrapper>
                <Spinner />
            </SpinnerWrapper>
        );
    }
    return <img src={data} alt={alt} className="w-full h-full object-cover" />;
};

export const PlanetThumbnail: React.FC<{ image_path: string }> = ({ image_path }) => {
    const { data, isFetching } = usePlanetThumbnail(image_path);
    return (
        <>
            {isFetching ? (
                <SpinnerWrapper>
                    <Spinner />
                </SpinnerWrapper>
            ) : (
                <StyledImage src={data} />
            )}
        </>
    );
};

export const TargetOfInterestImage: React.FC<{
    upsampleImage?: boolean;
    imageSource: ImageSourceValues;
    lon: number;
    lat: number;
    timestamp: string;
    mmsi: string;
    alt?: string;
}> = ({ upsampleImage, imageSource, lon, lat, timestamp, mmsi, alt }) => {
    const { data, isLoading } = useEarthEngineImage(
        lon,
        lat,
        new Date(timestamp),
        imageSource,
        upsampleImage || false,
        mmsi && timestamp ? `${mmsi}/${timestamp}` : ''
    );
    if (isLoading) {
        return (
            <SpinnerWrapper>
                <Spinner />
            </SpinnerWrapper>
        );
    }
    return <img src={data} alt={alt} className="w-full h-full object-cover" />;
};

function ConvertDDToDMS(D: unknown) {
    return [0 | D, '° ', 0 | (((D = (D < 0 ? -D : D) + 1e-4) % 1) * 60), "' ", 0 | (((D * 60) % 1) * 60), '"'].join('');
}

export const ReportPopup: React.FC<{ feature: unknown; reportType: ReportType; isAdmin?: boolean }> = ({
    feature,
    reportType,
    isAdmin,
}) => {
    const {
        image_source,
        timestamp,
        // correlated,
        object_class,
        // matching_label,
        length,
        lon,
        lat,
        object_class_score,
        ais_length,
        // estimation_score,
        // matching_score,
        course,
        speed,
        mmsi,
        image_path,
        // distance_to_mmsi,
        // ais_length,
        // time_to_nearest_ais,
    } = feature;
    let formattedTimestamp = new Date(timestamp).toUTCString(); //.substring(17);
    if (reportType === ReportType.InteractiveReport) {
        formattedTimestamp = timestamp.slice(11, 19);
    }
    return (
        <PopupDiv>
            <div onClick={() => console.log(lat + ', ' + lon)}>
                <b>Satellite:</b> {image_source && capitalizeFirstLetter(image_source)}
            </div>
            {lon && (
                <div>
                    <b>Longitude:</b> {ConvertDDToDMS(lon)}
                </div>
            )}
            {lat && (
                <div>
                    <b>Latitude:</b> {ConvertDDToDMS(lat)}
                </div>
            )}
            {object_class && (
                <div>
                    <b>Class:</b> {object_class && capitalizeFirstLetter(object_class)}
                </div>
            )}
            {object_class_score && (
                <div>
                    <b>Certainty:</b> {Number(object_class_score).toFixed(2)}
                </div>
            )}
            {mmsi && (
                <div>
                    <b>MMSI:</b> {mmsi}
                </div>
            )}
            {course && (
                <div>
                    <b>{mmsi ? 'AIS Course' : 'Course'}:</b> {course && Number(course).toFixed()}°
                </div>
            )}
            {speed && (
                <div>
                    <b>AIS Speed:</b> {speed && Number(speed).toFixed(1)} kn
                </div>
            )}
            {length && (
                <div>
                    <b>Predicted Length:</b> {Number(length).toFixed()} m
                </div>
            )}
            {ais_length && (
                <div>
                    <b>AIS Length:</b> {Number(ais_length).toFixed()} m
                </div>
            )}
            <div>
                <b>Timestamp:</b> {formattedTimestamp}
            </div>
            {image_path ? (
                <DetectionImage image_path={image_path} />
            ) : (
                <TargetOfInterestImage
                    imageSource={image_source}
                    lon={lon}
                    lat={lat}
                    timestamp={timestamp}
                    mmsi={mmsi}
                />
            )}
        </PopupDiv>
    );
};

export const GapPopup: React.FC<{ clearviewLink: string }> = ({ clearviewLink }) => {
    return (
        <PopupDiv>
            <LinkInPopup href={clearviewLink} target="_blank" background={'#750cf5'}>
                Locate dark vessel in gap with VAKE ClearView
            </LinkInPopup>
        </PopupDiv>
    );
};

export const TargetsOfInterestPopup: React.FC<{ feature: unknown; reportType: ReportType }> = ({
    feature,
    reportType,
}) => {
    return (
        <PopupDiv>
            {Object.entries(feature)
                .filter(([key, value]) => value)
                .filter(([key, value]) => !['object_class_score', 'uuid'].includes(key))
                .map(([key, value]) => (
                    <div key={key}>
                        <b>{capitalizeFirstLetter(formatKey(key))}:</b> {formatValue(key, value, reportType)}
                    </div>
                ))}
            {feature.image_path ? (
                <DetectionImage image_path={feature.image_path} />
            ) : (
                <TargetOfInterestImage
                    imageSource={feature.image_source}
                    lon={feature.lon}
                    lat={feature.lat}
                    timestamp={feature.timestamp}
                    mmsi={feature.mmsi}
                />
            )}
        </PopupDiv>
    );
};

export const TrackPopup: React.FC<{ feature: unknown }> = ({ feature }) => {
    const { mmsi, start_timestamp, end_timestamp } = feature;
    return (
        <PopupDiv>
            {mmsi && (
                <div>
                    <b>MMSI:</b> {mmsi}
                </div>
            )}
            {start_timestamp && (
                <div>
                    <b>Start:</b> {start_timestamp}
                </div>
            )}
            {end_timestamp && (
                <div>
                    <b>End:</b> {end_timestamp}
                </div>
            )}
        </PopupDiv>
    );
};

const formatKey = (key: string) => {
    key = key === 'image_source' ? 'Image source' : key;
    key = key === 'correlated' ? 'Correlation' : key;
    key = key === 'object_class' ? 'Object class' : key;
    key = key === 'matching_label' ? 'Status' : key;
    return key;
};

const formatValue = (key: string, value: unknown, reportType?: ReportType) => {
    value = key === 'timestamp' && reportType === ReportType.InteractiveReport ? value.slice(11, 19) : value;
    value = key === 'length' ? value && Number(value).toFixed(2) + ' m' : value;
    value = key === 'probability' ? Number(value) + ' %' : value;
    value = key === 'image_source' ? (value === 'sentinel1' ? 'Sentinel-1' : 'Sentinel-2') : value;
    value = key === 'time_to_nearest_ais' ? value && Number(value).toFixed(1) + ' s' : value;
    value = key === 'course' ? value && Number(value).toFixed() + '°' : value;
    value = key === 'correlated' ? (value ? 'Done' : 'Not done') : value;
    value = key === 'speed' ? Number(value) + ' kn' : value;
    return value;
};

const capitalizeFirstLetter = (string: string) => string.charAt(0).toUpperCase() + string.slice(1);

export const TimeInAcqusitionPopUp: React.FC<{
    acqusitionPopUp: AcquisitionPopUpInfo;
    setPopupInfo: (v: AcquisitionPopUpInfo | null) => void;
}> = ({ acqusitionPopUp, setPopupInfo }) => {
    const { lat, lon, timestamp, satellite } = acqusitionPopUp;
    const { data: timeAtPoint, isLoading, isError } = useTimeAtPointInAcqusition(timestamp, lon, lat);
    const text = timeAtPoint
        ? 'at ' + timeAtPoint.toISOString().slice(11, 16) + ' on ' + timeAtPoint.toISOString().slice(0, 10)
        : 'sometime in the next hours. Ask VAKE for details.';
    return (
        <Popup longitude={lon} latitude={lat} anchor="bottom" onClose={() => setPopupInfo(null)}>
            {isLoading && (
                <div className="flex justify-center">
                    <MiniSpinner size={30} />
                </div>
            )}
            {isError && <VText>Did not find time at point:/</VText>}
            {!isLoading && !isError && (
                <VText>
                    Satellite {satellite} passes {text}
                </VText>
            )}
        </Popup>
    );
};

export const DisplayImage: FC<{
    imagePath?: string;
    imageSource?: string;
    lon: number;
    lat: number;
    timestamp: string;
    mmsi: string;
}> = ({ imagePath, imageSource, lon, lat, timestamp, mmsi }) => {
    if (imageSource === ImageSource.SENTINEL2) {
        if (imagePath) {
            return <DetectionImage image_path={imagePath} />;
        } else {
            return (
                <TargetOfInterestImage
                    imageSource={imageSource}
                    lon={lon}
                    lat={lat}
                    timestamp={timestamp}
                    mmsi={mmsi}
                />
            );
        }
    } else if (imagePath && imageSource === ImageSource.PLANET_SCOPE) {
        // TODO: could potentially need a rewrite, have not tested planet scope in OW 2.0
        return <PlanetThumbnail image_path={imagePath} />;
    } else {
        return (
            <div className="flex items-center justify-center h-full w-full text-label-primary">
                <ImageBroken size={16} />
            </div>
        );
    }
};

export type OverwatchPopup = {
    lon: number;
    lat: number;
    content: ReactNode;
};
export const OverwatchPopup: FC<OverwatchPopup> = ({ lon, lat, content }) => {
    const [openActionMenu, setOpenActionMenu] = useState<boolean>(false);
    const latLonText = latLonTextFormat(lat, lon);
    const dmt = convertDecimalToDM(lat, lon);
    const url = new URL(window.location.href);
    const params = url.searchParams;
    params.set('latitude', lat.toString());
    params.set('longitude', lon.toString());
    params.delete('uuid');
    const finalUrl = url.toString();
    return (
        <Popup longitude={lon} latitude={lat} anchor="bottom">
            <div className="flex flex-col">
                <VText variant="H4">Position</VText>
                <div className="flex items-center">
                    <div className="flex-grow">
                        <VText variant="D1">Lat,Lon: {latLonText}</VText>
                    </div>
                    <Copy value={latLonText} />
                </div>
                <div className="flex items-center">
                    <div className="flex-grow">
                        <VText variant="D1">DMS: {dmt}</VText>
                    </div>
                    <Copy value={dmt} />
                </div>
                {content}
                <div className="flex justify-center">
                    <Button
                        text="Actions"
                        size="small"
                        iconBefore={<BinocularsIcon />}
                        iconAfter={openActionMenu ? <CaretUpIcon /> : <CaretDownIcon />}
                        onClick={() => setOpenActionMenu(!openActionMenu)}
                    />
                </div>
                {openActionMenu && (
                    <div className="flex flex-col gap-1 p-2">
                        <SendToMattermost
                            textOnButton={'Register Vessel'}
                            iconOnButton={<BoatIcon />}
                            textToSend={`Registered a vessel in this [Overwatch](${finalUrl}) at this latLon: ${latLonText}`}
                            successMessage={'Thank you! We will follow up with you shortly.'}
                            size="small"
                            variant="neutral"
                        />
                        <SendToMattermost
                            textOnButton={'Register Object'}
                            iconOnButton={<LifebuoyIcon />}
                            textToSend={`Registered an object in this [Overwatch](${finalUrl}) at this latLon: ${latLonText}`}
                            successMessage={'Thank you! We will follow up with you shortly.'}
                            size="small"
                            variant="neutral"
                        />
                        <SendToMattermost
                            textOnButton={'Request High-Res Image'}
                            iconOnButton={<ImageIcon />}
                            textToSend={`Requested a high-resolution image in this [Overwatch](${finalUrl}) at this latLon: ${latLonText}`}
                            successMessage={'Thank you! We will follow up with you shortly.'}
                            size="small"
                            variant="neutral"
                        />
                        <SendToMattermost
                            textOnButton={'Help Me'}
                            iconOnButton={<BinocularsIcon />}
                            textToSend={`Help requested in this [Overwatch](${finalUrl}) at this latLon: ${latLonText}`}
                            successMessage={'Thank you! We will follow up with you shortly.'}
                            size="small"
                            variant="neutral"
                        />
                    </div>
                )}
            </div>
        </Popup>
    );
};
